<template>
  <div v-if="name || address" class="account-list-item w-full pb-3">
    <div class="account-list-item__content b-rounded-1 overflow-hidden">
      <div
        :aria-label="`Account: ${name} (ID: ${accountId})`"
        class="account-header select-none flex items-center"
        :class="{ 'cursor-pointer': !!contacts?.length }"
        @click="onCollapse"
      >
        <div class="account-header__left">
          <CIcon
            v-if="contacts?.length"
            class="mx-3"
            fa-type="fas"
            :icon="collapsed ? 'angle-right' : 'angle-down'"
            :size="12"
          />
          <CIcon
            v-else-if="cardIcon"
            class="mx-4"
            :color="themeVars.primary4"
            fa-type="fas"
            :icon="cardIcon"
            :size="16"
          />
        </div>
        <div class="account-header__content truncate">
          <div
            v-tooltip="name"
            class="account-header__name font-600 underline truncate"
            v-html="highlight(name, (search || '').trim())"
          />

          <div v-if="address" class="account-header__address truncate">
            {{ address }}
          </div>
          <div
            v-if="customFieldValue != null"
            class="account-header__custom-field truncate"
            v-html="highlight(String(customFieldValue), (search || '').trim())"
          />
        </div>
        <div class="account-header__right h-full flex items-center justify-end">
          <div class="flex gap-1 items-center">
            <div v-if="lastViewedDate" class="header__right__last-date">
              {{ t('canvasUI.CContactAccountListItem.lastSeen') }}
              {{ formatDateTime(lastViewedDate, props.dateFormat || 'DD/MM/YYYY') }}
            </div>
            <!-- Alter disabled condition if we want to allow multiple account selection -->
            <CButton
              :aria-label="`${selectedAccountIds?.includes(accountId) ? 'Remove' : 'Add'} account: ${name} (ID: ${accountId})`"
              circle
              class="mr-2"
              :class="{ 'selected-contact-button-color': selectedAccountIds?.includes(accountId) }"
              :disabled="disableButton"
              @click.stop="
                selectedAccountIds?.includes(accountId)
                  ? emit('remove-account', accountId)
                  : emit('add-account', accountId)
              "
            >
              <CIcon fa-type="fas" :icon="selectedAccountIds?.includes(accountId) ? 'close' : 'plus'" />
            </CButton>
          </div>
        </div>
      </div>

      <div v-if="!collapsed" class="account-list-item__contacts">
        <div
          v-for="contact in contacts"
          :key="contact.Id"
          :aria-label="`Contact: ${contact.Name} (Account ID: ${accountId})`"
          class="account-list-item__contacts__list-item flex items-center select-none"
        >
          <div class="account-list-item__contacts__list-item__left">
            <CIcon class="mr-3" :color="themeVars.primary4" fa-type="fas" icon="address-card" :size="16" />
          </div>
          <div class="account-list-item__contacts__list-item__content">
            <div
              class="account-list-item__contacts__list-item__name font-600 underline truncate"
              v-html="highlight(contact.Name, (search || '').trim())"
            />

            <div
              v-if="contact.MailingAddress || contact.Email || contact.Phone"
              class="account-list-item__contacts__list-item__info truncate"
            >
              {{
                `${getAddress(contact.MailingAddress)}` +
                `${
                  (getAddress(contact.MailingAddress) && contact.Phone ? ` · ${contact.Phone}` : contact.Phone) ?? ''
                }` +
                `${
                  ((getAddress(contact.MailingAddress) || contact.Phone) && contact.Email
                    ? ` · ${contact.Email}`
                    : contact.Email) ?? ''
                }`
              }}
            </div>
          </div>
          <div class="account-list-item__contacts__list-item__right h-full flex items-center justify-end">
            <CButton
              :aria-label="`${selectedContactIds?.includes(contact.Id) ? 'Remove' : 'Add'} contact: ${contact.Name} (ID: ${contact.Id})`"
              circle
              class="mr-2"
              :class="{ 'selected-contact-button-color': selectedContactIds?.includes(contact.Id) }"
              :disabled="disableAddContactButton(contact.Id)"
              @click="
                selectedContactIds?.includes(contact.Id)
                  ? emit('remove-contact', contact.Id)
                  : emit('add-contact', contact.Id)
              "
            >
              <CIcon fa-type="fas" :icon="selectedContactIds?.includes(contact.Id) ? 'close' : 'plus'" />
            </CButton>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { highlight } from '@lib/util/string'
import tooltipDirective from '@lib/directives/tooltip'
import { formatDateTime } from '@lib/util/date'

const vTooltip = tooltipDirective

const props = defineProps<{
  accountId: string
  name: string
  address?: string
  customFieldValue?: string | number | boolean | null
  contacts?: any[]
  search?: string
  cardIcon?: string
  selectedAccountIds?: string[]
  selectedContactIds?: string[]
  loading: boolean
  allowAccountChange?: boolean
  allowContactsFromDifferentAccounts?: boolean
  restrictToSingleContact?: boolean
  lastViewedDate?: string
  dateFormat?: string
}>()

const emit = defineEmits<{
  (e: 'add-contact', contactId: string): void
  (e: 'add-account', accountId: string): void
  (e: 'remove-contact', contactId: string): void
  (e: 'remove-account', accountId: string): void
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const collapsed = ref(!props.search)

// Watch for changes in search prop
watch(
  () => props.search,
  (newSearch) => {
    // Only expand on initial search if not already expanded
    if (props.contacts?.length && newSearch && collapsed.value) {
      collapsed.value = false
    }
  },
)

const onCollapse = () => {
  if (props.contacts?.length) {
    collapsed.value = !collapsed.value
  }
}

const getAddress = (address?: {
  city?: string
  country?: string
  geocodeAccuracy?: string
  latitude?: number
  longitude?: number
  postalCode?: string
  state?: string
  street?: string
}) => {
  // TODO: use to extract proper address format and reuse for account as well (move to utils)
  return address?.street ?? ''
}

const disableButton = computed(() => {
  if (
    props.loading ||
    !props.allowAccountChange ||
    (props.selectedAccountIds?.length && !props.selectedAccountIds.includes(props.accountId))
  ) {
    return true
  }

  // If allowContactsFromDifferentAccounts is false, enforce account consistency
  if (!props.allowContactsFromDifferentAccounts && props.selectedContactIds?.length) {
    // Find the account ID of any selected contact by checking our local contacts
    const selectedContactFromThisAccount = props.contacts?.find((c) => props.selectedContactIds?.includes(c.Id))

    return !selectedContactFromThisAccount
  }

  return false
})

const disableAddContactButton = (contactId: string) => {
  // Always disable if loading
  if (props.loading) return true

  // If restrictToSingleContact is enabled and a contact is already selected, disable all other contacts
  if (props.restrictToSingleContact && props.selectedContactIds?.length) {
    // If this specific contact is already selected, allow button (for removal)
    return !props.selectedContactIds.includes(contactId)
  }

  // If allowContactsFromDifferentAccounts is true, don't restrict by account
  if (props.allowContactsFromDifferentAccounts) return false

  // If there's a selected account, only allow contacts from that account
  if (props.selectedAccountIds?.length) {
    return props.accountId !== props.selectedAccountIds[0]
  }

  // If there are selected contacts, only allow contacts from the same account as selected contacts
  if (props.selectedContactIds?.length) {
    // Check if any selected contacts are from this account
    const hasSelectedContactsFromThisAccount = props.contacts?.some((c) => props.selectedContactIds?.includes(c.Id))

    return !hasSelectedContactsFromThisAccount
  }

  return false
}
</script>

<style scoped>
:deep(.highlight) {
  background-color: v-bind('themeVars.info');
}

.account-list-item__content {
  background-color: v-bind('themeVars.primary6');
}

.selected-contact-button-color {
  background-color: v-bind('themeVars.primary5');
}

.account-header {
  height: 58px;
}

.account-header__left {
  flex: 0 0 36px;
}

.account-header__content {
  flex: 1;
  min-width: 0;
}

.account-header__right {
  flex: 0 0 48px;
  border-right: 4px solid v-bind('themeVars.primary3');
}

.header__right__last-date {
  min-width: 70px;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.4px;
  color: #757575;
  text-align: right;
  line-height: 120%;
}
.account-header__address {
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: v-bind('themeVars.text2');
}

.account-header__custom-field {
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: v-bind('themeVars.text2');
  font-style: italic;
}

.account-list-item__contacts {
  border-right: 4px solid v-bind('themeVars.primary3');
}

.account-list-item__contacts__list-item {
  height: 50px;
  padding-left: 36px;
  border-top: 1px solid v-bind('themeVars.primary5');
}

.account-list-item__contacts__list-item__left {
  flex: 0 0 36px;
}

.account-list-item__contacts__list-item__content {
  flex: 1;
  min-width: 0;
}

.account-list-item__contacts__list-item__right {
  flex: 0 0 48px;
}

.account-list-item__contacts__list-item__info {
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.25px;
  color: v-bind('themeVars.text2');
}
</style>
